import { Clear, Close, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Cell,
  Tooltip as ChartTooltip,
  LabelList,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import { setBubbleHeatmapGraphVariable } from '../../../../stores/slices/graphSlice';
import { darkGrey, hoverColor } from '../../../../util/colorHelper';
import { variableGraphOptions, variableGraphOptionsNG } from '../../../../util/convergenceVariablesOptions';
import { statKeys } from '../../../../util/drugClassesRules';
import { drugAcronyms, drugAcronymsOpposite } from '../../../../util/drugs';
import { getAxisLabel } from '../../../../util/genotypes';
import { longestVisualWidth } from '../../../../util/helpers';
import { isTouchDevice } from '../../../../util/isTouchDevice';
import { organismsWithLotsGenotypes } from '../../../../util/organismsCards';
import { heatmapLegendGradient, heatmapTextColor, mixColorScale } from '../../Map/mapColorHelper';
import { SelectCountry } from '../../SelectCountry';
import { useStyles } from './BubbleHeatmapGraphMUI';
import { PlottingOptionsHeader } from '../../Shared/PlottingOptionsHeader';
import { useTranslation } from 'react-i18next';

// Top axis area reserved for the rotated -45deg column labels on the first
// chart. Allocated via XAxis height so the cell row stays at the bottom of
// the first chart, immediately adjacent to row 2 below it.
const FIRST_ROW_AXIS_HEIGHT = 150;

const xOptionsByOrganism = [
  {
    label: 'Genotype',
    value: 'genotype',
    organisms: ['styphi', 'ngono', 'kpneumo', 'ecoli', 'shige', 'decoli', 'saureus', 'strepneumo'],
  },
  {
    label: 'Lineage',
    value: 'genotype',
    organisms: ['senterica', 'sentericaints'],
  },
];

export const BubbleHeatmapGraph2 = ({ showFilter, setShowFilter }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [xAxisType, setXAxisType] = useState('');
  const [yAxisType /*setYAxisType*/] = useState('resistance');
  const [xAxisSelected, setXAxisSelected] = useState([]);
  const [yAxisSelected, setYAxisSelected] = useState([]);
  const [genotypeSearch, setGenotypeSearch] = useState('');
  const [plotChart, setPlotChart] = useState(() => {});

  const dispatch = useAppDispatch();
  const organism = useAppSelector(state => state.dashboard.organism);
  const canGetData = useAppSelector(state => state.dashboard.canGetData);
  const mapData = useAppSelector(state => state.map.mapData);
  const mapRegionData = useAppSelector(state => state.map.mapRegionData);
  const actualCountry = useAppSelector(state => state.dashboard.actualCountry);
  const actualRegion = useAppSelector(state => state.dashboard.actualRegion);
  const canFilterData = useAppSelector(state => state.dashboard.canFilterData);
  const bubbleHeatmapGraphVariable = useAppSelector(state => state.graph.bubbleHeatmapGraphVariable);
  const loadingPDF = useAppSelector(state => state.dashboard.loadingPDF);
  const organismHasLotsOfGenotypes = useMemo(() => organismsWithLotsGenotypes.includes(organism), [organism]);

  const xOptions = useMemo(() => {
    return xOptionsByOrganism.filter(x => x.organisms.includes(organism));
  }, [organism]);

  useEffect(() => {
    setXAxisType(xOptions[0]?.value ?? '');
  }, [xOptions]);

  const selectedCRData = useMemo(() => {
    return (actualCountry !== 'All' ? mapData : mapRegionData).find(
      x => x.name === (actualCountry !== 'All' ? actualCountry : actualRegion),
    );
  }, [actualCountry, actualRegion, mapData, mapRegionData]);

  const resistanceOptions = useMemo(() => {
    const options = statKeys[organism] ? statKeys[organism] : statKeys['others'];
    const resistance = options.filter(option => option.resistanceView).map(option => option.name);

    if (organism === 'ngono') {
      resistance.splice(5, 0, ...['XDR', 'MDR']);
    }

    const drugs = {};

    resistance.forEach(drug => {
      const stats = selectedCRData?.stats;

      if (!stats) return;

      if (!(drug in drugs)) {
        drugs[drug] = stats[drug]?.count ?? 0;
        return;
      }

      drugs[drug] += stats[drug]?.count ?? 0;
    });

    return (
      Object.entries(drugs)
        .filter(x => x[1] > 0 || x[0] === 'Pansusceptible')
        .map(x => x[0]) ?? []
    );
  }, [organism, selectedCRData?.stats]);

  const markersOptions = useMemo(() => {
    const drugKey = yAxisType === 'kp-markers-carbapenems' ? 'Carbapenems' : 'ESBL';

    return (
      selectedCRData?.stats[drugKey]?.items
        .filter(x => x.name !== '-')
        .sort((a, b) => b.count - a.count)
        .map(x => x.name) ?? []
    );
  }, [selectedCRData?.stats, yAxisType]);

  const statColumn = useMemo(() => {
    switch (xAxisType) {
      case 'genotype':
        if (organism === 'kpneumo') {
          const variableOption = variableGraphOptions.find(x => x.value === bubbleHeatmapGraphVariable);
          if (!variableOption) {
            console.warn(
              `BubbleHeatmapGraph2: bubbleHeatmapGraphVariable '${bubbleHeatmapGraphVariable}' not found in variableGraphOptions. Falling back to 'GENOTYPE'.`,
            );
            return 'GENOTYPE';
          }
          return variableOption.mapValue;
        }
        if (organism === 'ngono') {
          const variableOption = variableGraphOptionsNG.find(x => x.value === bubbleHeatmapGraphVariable);
          if (!variableOption) {
            console.warn(
              `BubbleHeatmapGraph2: bubbleHeatmapGraphVariable '${bubbleHeatmapGraphVariable}' not found in variableGraphOptions. Falling back to 'GENOTYPE'.`,
            );
            return 'GENOTYPE';
          }
          return variableOption.mapValue;
        }
        return 'GENOTYPE';
      case 'pathotype':
        return 'PATHOTYPE';
      default:
        return '';
    }
  }, [bubbleHeatmapGraphVariable, organism, xAxisType]);

  const xAxisOptions = useMemo(() => {
    return selectedCRData?.stats[statColumn]?.items?.map(x => x.name) ?? [];
  }, [selectedCRData?.stats, statColumn]);

  const filteredXAxisOptions = useMemo(() => {
    const filteredOptions = xAxisOptions.filter(option => option.toLowerCase().includes(genotypeSearch.toLowerCase()));

    // if (!organismHasLotsOfGenotypes) {
    //   return filteredOptions;
    // }

    return filteredOptions.slice(0, 20);
  }, [xAxisOptions, organismHasLotsOfGenotypes, genotypeSearch]);

  const yAxisOptions = useMemo(() => {
    switch (yAxisType) {
      case 'resistance':
        return resistanceOptions;
      case 'kp-markers-carbapenems':
      case 'kp-markers-esbl':
        return markersOptions;
      default:
        return [];
    }
  }, [markersOptions, resistanceOptions, yAxisType]);

  useEffect(() => {
    setXAxisSelected(xAxisOptions?.slice(0, 10));
  }, [xAxisOptions]);

  useEffect(() => {
    setYAxisSelected(yAxisOptions);
  }, [yAxisOptions]);

  const formatDrugLabel = useCallback(
    name => (yAxisType === 'resistance' ? drugAcronymsOpposite[drugAcronyms[name] ?? name] ?? name : name),
    [yAxisType],
  );

  const yAxisWidth = useMemo(() => {
    return longestVisualWidth((yAxisSelected ?? []).map(formatDrugLabel));
  }, [yAxisSelected, formatDrugLabel]);

  const getOptionLabel = useCallback(
    item => {
      const totalCount = selectedCRData?.stats[statColumn].items.find(x => x.name === item)?.count ?? 0;

      return `${item} (total N=${totalCount})`;
    },
    [selectedCRData?.stats, statColumn],
  );

  const getOptionLabelY = useCallback(
    item => {
      if (yAxisType === 'resistance') {
        return drugAcronymsOpposite[drugAcronyms[item] ?? item] ?? item;
      }

      return item;
    },
    [yAxisType],
  );

  function handleChangeXAxisSelected({ event = null, all = false }) {
    const value = event?.target.value;

    if (value?.length === 1 && value[0] === undefined) {
      return;
    }

    if (!all) {
      setXAxisSelected(value);
      return;
    }

    if (
      xAxisSelected.length === filteredXAxisOptions.length ||
      xAxisSelected.some(x => !xAxisSelected.slice(0, 20).includes(x))
    ) {
      setXAxisSelected([]);
      return;
    }

    setXAxisSelected(filteredXAxisOptions);
  }

  function handleChangeYAxisSelected({ event = null, all = false }) {
    const value = event?.target.value;

    if (!all) {
      setYAxisSelected(value);
      return;
    }

    if (yAxisOptions?.length === yAxisSelected?.length) {
      setYAxisSelected([]);
      return;
    }

    setYAxisSelected(yAxisOptions?.slice());
  }

  function hangleChangeSearch(event) {
    event.stopPropagation();
    setGenotypeSearch(event.target.value);
  }

  function clearSearch(event) {
    event.stopPropagation();
    setGenotypeSearch('');
  }

  function handleChangeVariable(event) {
    dispatch(setBubbleHeatmapGraphVariable(event.target.value));
  }

  const getTitle = useCallback(value => {
    return drugAcronymsOpposite[value] ?? Object.keys(drugAcronyms).find(key => drugAcronyms[key] === value) ?? value;
  }, []);

  const configuredMapData = useMemo(() => {
    if (!selectedCRData || yAxisSelected.length === 0 || xAxisSelected.length === 0) {
      return [];
    }

    const matchingItems =
      selectedCRData?.stats[statColumn]?.items?.filter(item => xAxisSelected?.includes(item.name)) ?? [];

    if (yAxisType === 'resistance') {
      const drugRows = yAxisSelected.map(drug => {
        const row = { name: drug, items: [] };

        matchingItems.forEach(item => {
          const drugCount = item.drugs[drug]?.count ?? 0;
          const pct = item.count ? Number(((drugCount / item.count) * 100).toFixed(2)) : 0;
          row.items.push({
            itemName: item.name,
            percentage: pct,
            count: drugCount,
            index: 1,
            typeName: item.name,
            total: item.count,
            drug,
          });
        });

        return row;
      });

      // Ordering rules previously applied to the drug column axis now apply
      // to the drug row axis. Match by acronym so we stay consistent with the
      // existing styphi/sentericaints rules (CRO/CipR/CipNS first) and the
      // global rules (MDR/XDR/PAN/SUS last).
      const acronymOf = name => drugAcronyms[name] ?? name;
      drugRows.sort((a, b) => acronymOf(a.name).localeCompare(acronymOf(b.name)));

      const moveToStart = acronym => {
        const i = drugRows.findIndex(x => acronymOf(x.name) === acronym);
        if (i >= 0) drugRows.unshift(...drugRows.splice(i, 1));
      };

      const moveToEnd = acronym => {
        const i = drugRows.findIndex(x => acronymOf(x.name) === acronym);
        if (i >= 0) drugRows.push(...drugRows.splice(i, 1));
      };

      if (['styphi', 'sentericaints'].includes(organism)) {
        ['CRO', 'CipR', 'CipNS'].forEach(moveToStart);
      }
      ['MDR', 'XDR', 'PAN', 'SUS'].forEach(moveToEnd);

      return drugRows;
    }

    if (yAxisType.includes('markers')) {
      const drugKey = yAxisType.includes('esbl') ? 'ESBL' : 'Carbapenems';
      return yAxisSelected.map(gene => {
        const row = { name: gene.replace(' + ', '/'), items: [] };
        matchingItems.forEach(item => {
          const drugGenes = item?.drugs[drugKey]?.items ?? [];
          const currentGene = drugGenes.find(dg => dg.name === gene);
          row.items.push({
            itemName: item.name,
            percentage: currentGene?.percentage ?? 0,
            index: 1,
            typeName: item.name,
            total: item.count,
            drug: gene,
          });
        });
        return row;
      });
    }

    return [];
  }, [organism, selectedCRData, statColumn, xAxisSelected, yAxisSelected, yAxisType]);

  useEffect(() => {
    if (canGetData) {
      setPlotChart(() => {
        return (
          <>
            {configuredMapData?.map((item, index) => {
              return (
                <ResponsiveContainer
                  key={`heatmap-graph-${index}`}
                  width={yAxisWidth + 65 * xAxisSelected.length}
                  height={index === 0 ? 65 + FIRST_ROW_AXIS_HEIGHT : 65}
                >
                  <ScatterChart cursor={isTouchDevice() ? 'default' : 'pointer'} margin={{ top: 0, bottom: 0 }}>
                    <XAxis
                      type="category"
                      dataKey="itemName"
                      interval={0}
                      height={index === 0 ? FIRST_ROW_AXIS_HEIGHT : 0}
                      tick={
                        index === 0
                          ? props => {
                              const title = getTitle(props.payload.value);
                              return (
                                <Tooltip title={title} placement="top">
                                  <text
                                    x={props.x}
                                    y={props.y}
                                    fontSize="14px"
                                    dy={-10}
                                    textAnchor="start"
                                    transform={`rotate(-45, ${props.x}, ${props.y})`}
                                    fill="rgb(128,128,128)"
                                  >
                                    {props.payload.value}
                                  </text>
                                </Tooltip>
                              );
                            }
                          : false
                      }
                      axisLine={false}
                      orientation="top"
                    />

                    <YAxis
                      type="number"
                      dataKey="index"
                      name={item.name.toLowerCase()}
                      tick={false}
                      tickLine={false}
                      axisLine={false}
                      domain={[1, 1]}
                      label={{ value: formatDrugLabel(item.name), position: 'insideRight' }}
                      width={yAxisWidth}
                    />

                    <ZAxis type="number" dataKey="percentage" range={[3500, 3500]} />

                    <ChartTooltip
                      cursor={{ fill: hoverColor }}
                      wrapperStyle={{ zIndex: 100 }}
                      offset={40}
                      content={({ payload, active }) => {
                        if (payload !== null && active) {
                          const title = getTitle(payload[0]?.payload.drug ?? payload[0]?.payload.itemName);
                          return (
                            <div
                              className={classes.chartTooltipLabel}
                              style={{
                                marginTop: index + 1 === configuredMapData.length ? -40 : 0,
                              }}
                            >
                              <Typography variant="body1" fontWeight="500">
                                {payload[0]?.payload.typeName}
                              </Typography>
                              <Typography variant="caption" fontWeight="500">
                                Total: {payload[0]?.payload.total}
                              </Typography>
                              <Typography variant="body2">
                                {yAxisType !== 'resistance'
                                  ? `${title}: ${payload[0]?.payload.percentage}%`
                                  : `${title}: ${payload[0]?.payload.count} (${payload[0]?.payload.percentage}%)`}
                              </Typography>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />

                    <Scatter data={item.items} shape="square">
                      {item.items.map((option, index) => (
                        <Cell
                          name={option.drug}
                          key={`bubble-cell-${index}`}
                          fill={option.percentage === 0 ? darkGrey : mixColorScale(option.percentage)}
                        />
                      ))}
                      <LabelList
                        dataKey="percentage"
                        fontSize={12}
                        content={({ x, y, value, z }) => {
                          return (
                            <text
                              x={x + 33}
                              y={y + 38}
                              textAnchor="middle"
                              fontSize={15}
                              fontWeight={600}
                              fill={heatmapTextColor(value)}
                              pointerEvents="none"
                            >
                              {value}
                            </text>
                          );
                        }}
                      />
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              );
            })}
          </>
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredMapData, yAxisWidth]);

  return (
    <CardContent className={classes.bubbleHeatmapGraph}>
      <div className={classes.graphWrapper}>
        <div className={classes.graph} style={loadingPDF ? { overflow: 'visible' } : undefined} id="HSG2">
          {plotChart}
        </div>
      </div>
      {yAxisSelected.length === 0 && (
        <Box className={classes.nothingSelected}>
          <Typography fontWeight={600}>{t('common.noDataToShow')}</Typography>
        </Box>
      )}
      <Divider className={classes.divider} />
      <div className={classes.bottomLegend}>
        <div className={classes.legend}>
          <Typography fontSize="0.75rem">0%</Typography>
          <Box className={classes.singleBox} sx={{ backgroundColor: darkGrey }} />
        </div>
        <div className={classes.legend}>
          <Typography fontSize="0.75rem">1%</Typography>
          <Box className={classes.gradientBox} style={{ backgroundImage: heatmapLegendGradient() }} />
          <Typography fontSize="0.75rem">100%</Typography>
        </div>
      </div>
      {showFilter && !canFilterData && (
        <Box className={classes.floatingFilter}>
          <Card elevation={3}>
            <CardContent>
              <PlottingOptionsHeader onClose={() => setShowFilter(false)} className={classes.titleWrapper} />
              <div className={classes.selectsWrapper}>
                <SelectCountry />
                <div className={classes.selectPreWrapper}>
                  {organism === 'kpneumo' && (
                    <div className={classes.selectWrapper}>
                      <div className={classes.labelWrapper}>
                        <Typography variant="caption">{t('common.selectGenotype')}</Typography>
                      </div>
                      <Select
                        value={bubbleHeatmapGraphVariable}
                        onChange={handleChangeVariable}
                        inputProps={{ className: classes.selectInput }}
                        MenuProps={{ classes: { list: classes.selectMenu } }}
                        disabled={organism === 'none'}
                      >
                        {variableGraphOptions.map((option, index) => {
                          return (
                            <MenuItem key={index + 'bubble-heatmap-variable'} value={option.value}>
                              {option.label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </div>
                  )}
                  {organism === 'ngono' && (
                    <div className={classes.selectWrapper}>
                      <div className={classes.labelWrapper}>
                        <Typography variant="caption">Select variable</Typography>
                      </div>
                      <Select
                        value={bubbleHeatmapGraphVariable}
                        onChange={handleChangeVariable}
                        inputProps={{ className: classes.selectInput }}
                        MenuProps={{ classes: { list: classes.selectMenu } }}
                        disabled={organism === 'none'}
                      >
                        {variableGraphOptionsNG.map((option, index) => {
                          return (
                            <MenuItem key={index + 'bubble-heatmap-variable'} value={option.value}>
                              {option.label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </div>
                  )}
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">
                        Select {getAxisLabel(organism, bubbleHeatmapGraphVariable)}
                      </Typography>
                      <Tooltip
                        title={`If the total ${getAxisLabel(
                          organism,
                          bubbleHeatmapGraphVariable,
                        )} are too many, only the first 20 options are shown at a time`}
                        placement="top"
                      >
                        <InfoOutlined color="action" fontSize="small" className={classes.labelTooltipIcon} />
                      </Tooltip>
                    </div>
                    <Select
                      multiple
                      value={xAxisSelected}
                      onChange={event => handleChangeXAxisSelected({ event })}
                      displayEmpty
                      disabled={organism === 'none'}
                      endAdornment={
                        <Button
                          variant="outlined"
                          className={classes.selectButton}
                          onClick={() => handleChangeXAxisSelected({ all: true })}
                          disabled={organism === 'none'}
                          color={
                            xAxisSelected.length === filteredXAxisOptions.length ||
                            xAxisSelected.some(x => !xAxisOptions.slice(0, 20).includes(x))
                              ? 'error'
                              : 'primary'
                          }
                        >
                          {xAxisSelected.length === filteredXAxisOptions.length ||
                          xAxisSelected.some(x => !xAxisOptions.slice(0, 20).includes(x))
                            ? t('common.clearAll')
                            : t('common.select20')}
                        </Button>
                      }
                      inputProps={{ className: classes.multipleSelectInput }}
                      MenuProps={{
                        disableAutoFocusItem: true,
                        classes: { paper: classes.menuPaper, list: classes.selectMenu },
                      }}
                      renderValue={selected => <div>{`${t('common.selectedOfTotal', { selected: selected?.length ?? 0, total: xAxisOptions?.length ?? 0 })}`}</div>}
                      onClose={clearSearch}
                    >
                      <Box
                        className={classes.selectSearch}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <TextField
                          variant="standard"
                          placeholder={'Search for more...'}
                          fullWidth
                          value={genotypeSearch}
                          onChange={hangleChangeSearch}
                          InputProps={
                            genotypeSearch === ''
                              ? undefined
                              : {
                                  endAdornment: (
                                    <IconButton size="small" onClick={clearSearch}>
                                      <Clear />
                                    </IconButton>
                                  ),
                                }
                          }
                        />
                      </Box>
                      {filteredXAxisOptions?.map((option, index) => (
                        <MenuItem key={`geo-x-axis-option-${index}`} value={option}>
                          <Checkbox checked={xAxisSelected?.indexOf(option) > -1} />
                          <ListItemText primary={getOptionLabel(option)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className={classes.selectPreWrapper}>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">{t('common.selectDrug')}</Typography>
                    </div>
                    <Select
                      multiple
                      value={yAxisSelected}
                      onChange={event => handleChangeYAxisSelected({ event })}
                      displayEmpty
                      disabled={organism === 'none'}
                      endAdornment={
                        <Button
                          variant="outlined"
                          className={classes.selectButton}
                          onClick={() => handleChangeYAxisSelected({ all: true })}
                          disabled={organism === 'none'}
                          color={yAxisSelected?.length === yAxisOptions?.length ? 'error' : 'primary'}
                        >
                          {yAxisSelected?.length === yAxisOptions?.length ? t('common.clearAll') : t('common.selectAll')}
                        </Button>
                      }
                      inputProps={{ className: classes.multipleSelectInput }}
                      MenuProps={{
                        classes: { paper: classes.menuPaper, list: classes.selectMenu },
                      }}
                      renderValue={selected => <div>{`${selected?.length} of ${yAxisOptions.length} selected`}</div>}
                    >
                      {yAxisOptions.map((option, index) => (
                        <MenuItem key={`geo-y-axis-option-${index}`} value={option}>
                          <Checkbox checked={yAxisSelected.indexOf(option) > -1} />
                          <ListItemText primary={getOptionLabelY(option)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Box>
      )}
    </CardContent>
  );
};
